"use client"

import { useCallback, useEffect, useState } from 'react'

interface NetworkStatus {
  isOnline: boolean
  hasInternetAccess: boolean
  isChecking: boolean
}

/**
 * ONE PROBE, HOWEVER MANY CALLERS (measured 2026-09-12).
 *
 * `NetworkStatusIndicator.tsx` defines two components that each call this hook, and both are mounted
 * from the root layout — so every page load fired TWO `/api/health` HEADs. They landed inside the
 * dashboard's opening burst and both timed out against their own 3s budget, because the server was
 * busy answering the calls the page actually needs. Two probes, both failing, both caused by the
 * thing they were measuring.
 *
 * Collapsing them here rather than at the call sites means a third consumer cannot reintroduce it.
 */
let inflightProbe: Promise<boolean> | null = null

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(() => {
    // Check if we're in browser environment
    if (typeof navigator !== 'undefined') {
      return navigator.onLine
    }
    return true // Assume online during SSR
  })
  
  const [hasInternetAccess, setHasInternetAccess] = useState(true) // Start optimistic
  const [isChecking, setIsChecking] = useState(false)
  
  // Check actual internet connectivity with a same-origin probe.
  const checkInternetAccess = useCallback(async (): Promise<boolean> => {
    if (!isOnline) return false

    setIsChecking(true)
    try {
      // Share one in-flight request across every hook instance.
      const healthResponse = await (inflightProbe
        ? inflightProbe.then(() => ({ ok: true } as Response))
        : (inflightProbe = fetch('/api/health', {
            method: 'HEAD',
            cache: 'no-cache',
            signal: AbortSignal.timeout(3000),
          }).then((r) => r.ok).finally(() => { inflightProbe = null }))
            .then((ok) => ({ ok } as Response)))

      setIsChecking(false)
      // If the endpoint exists and answers, we have connectivity.
      // If it returns a non-200 (e.g. 404 because the route is missing),
      // the server is still reachable — fall back to navigator.onLine.
      return healthResponse.ok || isOnline
    } catch {
      // Network error or timeout — fall back to navigator.onLine.
      setIsChecking(false)
      return isOnline
    }
  }, [isOnline])

  // Update internet access status
  const updateInternetAccess = useCallback(async () => {
    const hasAccess = await checkInternetAccess()
    setHasInternetAccess(hasAccess)
  }, [checkInternetAccess])

  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return

    const handleOnline = () => {
      console.log('Network: Browser reports online')
      setIsOnline(true)
      updateInternetAccess()
    }

    const handleOffline = () => {
      console.log('Network: Browser reports offline')
      setIsOnline(false)
      setHasInternetAccess(false)
    }

    // Listen to browser online/offline events
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // NOT DURING FIRST PAINT (measured 2026-09-12). This probe fired immediately on mount, landed
    // inside the dashboard's opening burst, and TIMED OUT against its own 3s budget — twice, ~2.9s
    // each — because the server was busy answering the five calls the page actually needs. A
    // connectivity check that fails because the app is loading is worse than useless: it competes
    // for the connections it is measuring, and then reports a problem it caused.
    //
    // `hasInternetAccess` starts optimistic, so deferring costs nothing. `requestIdleCallback` runs
    // it once the browser has nothing better to do; the timeout is the Safari fallback.
    const ric = (window as Window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number
    }).requestIdleCallback
    const idle = ric
      ? ric(() => updateInternetAccess(), { timeout: 8000 })
      : window.setTimeout(() => updateInternetAccess(), 4000)

    // Periodic internet access check (every 2 minutes when online, less aggressive)
    const intervalId = setInterval(() => {
      if (isOnline) {
        updateInternetAccess()
      }
    }, 120000) // 2 minutes instead of 30 seconds

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      clearInterval(intervalId)
      // The deferred first check must not outlive the component that scheduled it.
      const cic = (window as Window & { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback
      // `ric` is the capability test: if it existed, `idle` is an idle handle; if not, a timeout id.
      if (ric !== undefined && cic !== undefined) cic(idle)
      else window.clearTimeout(idle)
    }
  }, [isOnline, updateInternetAccess])

  return {
    isOnline,
    hasInternetAccess,
    isChecking
  }
}
